/**
 * Answer quality: the labelled suite, and real answers.
 *
 * Two panels because they are two jobs. The suite is fifty questions somebody
 * wrote, and the job there is comparison — which is why its cases open
 * independently and several can be read side by side. The response panel is
 * hundreds of real answers, and the job there is triage, which wants one
 * detail pane and a filter.
 *
 * Split out of AdminScreen when it outgrew a section of it.
 */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { type CSSProperties, useEffect, useState } from "react";
import {
  api,
  type EvalCase,
  type EvalSummary,
  type ResponseListResponse,
  type ResponseRow,
  type ResponseScoreView,
} from "../../lib/api";
import { formatNumber, timeAgo } from "../../lib/format";
import {
  Bar,
  Banner,
  Card,
  Empty,
  ErrorNote,
  Metric,
  Spinner,
  useToast,
} from "../../components/ui";

/**
 * Tones for the per-case chips. Inline hex, matching the pills already on this
 * screen rather than introducing a palette the rest of the file does not use.
 */
const CHIP_TONE: Record<string, CSSProperties> = {
  good: { background: "#E6F7DC", color: "#2F6420" },
  bad: { background: "#FFECEC", color: "#B23438" },
  //: Grey, and deliberately not the same grey as neutral: an unmeasured metric
  //: is a gap in the measurement, not a value without a verdict.
  unmeasured: { background: "#EFECEE", color: "#A49DA8" },
  neutral: { background: "#EFECEE", color: "#57515B" },
};

function Chip({ text, tone, title }: { text: string; tone: string; title?: string }) {
  return (
    <span className="pill" style={CHIP_TONE[tone] ?? CHIP_TONE.neutral} title={title}>
      {text}
    </span>
  );
}

/**
 * A structural check. `null` renders "n/a", never a cross.
 *
 * A question that must be declined is never asked to cite, and a case with no
 * expected document cannot miss one. The run-level bars exclude both from
 * their denominators for the same reason, so showing a failure here would
 * contradict the numbers above.
 */
function Verdict({ label, state }: { label: string; state: boolean | null }) {
  const tone = state === null ? "unmeasured" : state ? "good" : "bad";
  const word = state === null ? "n/a" : state ? "yes" : "no";
  return (
    <span className="flex items-center gap-1.5 text-sm text-muted-strong">
      <Chip text={word} tone={tone} />
      {label}
    </span>
  );
}

/** The one chip whose meaning depends on what the case was asked to do. */
function citationChip(entry: EvalCase): { text: string; tone: string } {
  if (entry.expectRefusal) {
    if (entry.refusedCorrectly === null) return { text: "not run", tone: "unmeasured" };
    return entry.refusedCorrectly
      ? { text: "declined", tone: "good" }
      : { text: "answered anyway", tone: "bad" };
  }
  if (entry.citedExpectedDocument !== null && entry.mustCiteDocNo) {
    return entry.citedExpectedDocument
      ? { text: `cited ${entry.mustCiteDocNo}`, tone: "good" }
      : { text: `missed ${entry.mustCiteDocNo}`, tone: "bad" };
  }
  if (entry.hasCitation === null) return { text: "not run", tone: "unmeasured" };
  return {
    text: entry.citationCount === 1 ? "1 citation" : `${entry.citationCount} citations`,
    tone: entry.hasCitation ? "good" : "bad",
  };
}

function CaseRow({
  entry,
  open,
  onToggle,
}: {
  entry: EvalCase;
  open: boolean;
  onToggle: () => void;
}) {
  const cite = citationChip(entry);
  return (
    <li className="border-b border-line-soft last:border-0">
      <button
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full flex-wrap items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-hover"
      >
        <svg
          aria-hidden
          viewBox="0 0 24 24"
          width="12"
          height="12"
          className={`mt-1.5 shrink-0 text-muted transition-transform ${open ? "rotate-90" : ""}`}
        >
          <path d="m9 18 6-6-6-6" fill="none" stroke="currentColor" strokeWidth="2.5" />
        </svg>
        <span className="min-w-0 flex-1">
          <span className="block text-base text-ink">{entry.question}</span>
          {entry.reason && (
            <span className="mt-0.5 block text-2xs text-muted">{entry.reason}</span>
          )}
          <span className="mt-1.5 flex flex-wrap gap-1">
            {entry.latencyMs !== null && (
              <Chip text={`${(entry.latencyMs / 1000).toFixed(1)}s`} tone="neutral" />
            )}
            <Chip text={cite.text} tone={cite.tone} />
            {entry.metrics.map((m) => (
              <Chip
                key={m.key}
                text={`${m.shortLabel} ${m.display}`}
                tone={m.tone}
                title={`${m.label}: ${m.display}${m.detail ? ` — ${m.detail}` : ""}`}
              />
            ))}
          </span>
        </span>
        {entry.expectRefusal && <Chip text="Must refuse" tone="neutral" />}
        <Chip
          text={entry.passed === null ? "Not run" : entry.passed ? "Pass" : "Fail"}
          tone={entry.passed === null ? "unmeasured" : entry.passed ? "good" : "bad"}
        />
      </button>

      {open && (
        <div className="border-t border-line-softest bg-surface-subtle px-4 py-3">
          <p className="mb-3 text-2xs text-muted-soft">{entry.expected}</p>

          <div className="mb-3 flex flex-wrap gap-x-5 gap-y-1.5">
            <Verdict label="Citation present" state={entry.hasCitation} />
            <Verdict
              label={
                entry.mustCiteDocNo ? `Cited ${entry.mustCiteDocNo}` : "Cited the expected document"
              }
              state={entry.citedExpectedDocument}
            />
            <Verdict label="Correct refusal" state={entry.refusedCorrectly} />
          </div>

          {entry.judgeError ? (
            <p className="mb-3 text-sm text-danger">Judging failed: {entry.judgeError}</p>
          ) : entry.metrics.length === 0 ? (
            <p className="mb-3 text-sm text-muted">Not judged.</p>
          ) : (
            <div className="mb-3">
              {entry.metrics.map((m) => (
                <Bar
                  key={m.label}
                  label={m.label}
                  value={m.display}
                  pct={m.pct}
                  colour={m.tone === "good" ? "#8DE971" : m.tone === "bad" ? "#B23438" : "#D9D3DB"}
                  note={m.detail}
                />
              ))}
            </div>
          )}

          {entry.answerText && (
            <>
              <div className="eyebrow mb-1 !text-muted">Answer</div>
              <p className="mb-3 whitespace-pre-wrap rounded-card bg-surface px-3 py-2 text-sm text-ink">
                {entry.answerText}
              </p>
            </>
          )}

          {entry.contextLocators.length > 0 && (
            <>
              <div className="eyebrow mb-1 !text-muted">Passages in the prompt</div>
              <ol className="mb-3 space-y-0.5">
                {entry.contextLocators.map((locator, n) => (
                  <li key={`${locator}-${n}`} className="text-2xs text-muted">
                    [{n + 1}] {locator}
                  </li>
                ))}
              </ol>
            </>
          )}

          <p className="text-2xs text-muted-soft">
            {[
              entry.citations.length > 0 ? `cited ${entry.citations.join(", ")}` : null,
              entry.contextCount !== null && entry.retrievedCount !== null
                ? `${entry.contextCount} of ${entry.retrievedCount} retrieved passages in the prompt`
                : null,
              entry.promptVersion !== null ? `prompt v${entry.promptVersion}` : null,
              entry.judgeCalls ? `${entry.judgeCalls} judge calls` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        </div>
      )}
    </li>
  );
}

/** The two chips a response row shows at a glance. */
const HEADLINE_KEYS = ["faithfulness", "answerRelevancy"];


function ResponseListRow({
  row,
  selected,
  onSelect,
}: {
  row: ResponseRow;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        onClick={onSelect}
        className={`flex w-full flex-col gap-1 border-b border-line-soft px-3 py-2.5 text-left transition-colors last:border-0 hover:bg-surface-hover ${
          selected ? "bg-surface-hover" : ""
        }`}
      >
        <span className="line-clamp-2 text-sm text-ink">{row.question}</span>
        <span className="text-2xs text-muted-soft">
          {row.author} · {timeAgo(row.askedAt)}
          {row.scope.length > 0 && ` · ${row.scope.join(", ")}`}
        </span>
        <span className="flex flex-wrap gap-1">
          {row.refused && <Chip text="declined" tone="neutral" />}
          {row.feedback && (
            <Chip
              text={row.feedback === "up" ? "rated up" : "rated down"}
              tone={row.feedback === "up" ? "good" : "bad"}
            />
          )}
          {row.scoreState === "pending" && <Chip text="pending…" tone="neutral" />}
          {row.scoreState === "failed" && (
            <Chip text="failed" tone="bad" title={row.scoreError ?? ""} />
          )}
          {row.scoreState === "scored" &&
            /* Both slots always, so an unmeasured "F —" is visibly different
               from a measured "F 91%" rather than simply absent. */
            HEADLINE_KEYS.map((key) => {
              const metric = row.metrics.find((m) => m.key === key);
              if (!metric) return null;
              return (
                <Chip
                  key={key}
                  text={`${metric.shortLabel} ${metric.display}`}
                  tone={metric.tone}
                  title={`${metric.label}: ${metric.display}${
                    metric.detail ? ` — ${metric.detail}` : ""
                  }`}
                />
              );
            })}
        </span>
      </button>
    </li>
  );
}

function ResponseDetail({
  row,
  live,
  onScore,
  scoring,
  blocked,
  atLimit,
}: {
  row: ResponseRow;
  live: ResponseScoreView | undefined;
  onScore: () => void;
  scoring: boolean;
  blocked: string | null;
  atLimit: boolean;
}) {
  // The poll result wins while it is fresher than the page behind it.
  const metrics = live?.metrics?.length ? live.metrics : row.metrics;
  const state = live?.state ?? row.scoreState;
  const note = live?.contextNote || row.contextNote;

  const disabled =
    scoring || state === "pending" || row.refused || !!blocked || atLimit;
  const why = row.refused
    ? "A declined answer has no answer to judge."
    : blocked
      ? blocked
      : atLimit
        ? "The daily scoring limit has been reached."
        : undefined;

  return (
    <div className="min-w-0">
      <div className="eyebrow mb-1 !text-muted">Question</div>
      <p className="mb-3 text-base text-ink">{row.question}</p>
      {row.resolvedQuestion && (
        <p className="mb-3 text-2xs text-muted">
          Read as: {row.resolvedQuestion}
        </p>
      )}

      <div className="eyebrow mb-1 !text-muted">Answer</div>
      {row.answerText ? (
        <p className="mb-3 whitespace-pre-wrap rounded-card bg-surface px-3 py-2 text-sm text-ink">
          {row.answerText}
        </p>
      ) : (
        <p className="mb-3 text-sm text-muted-soft">
          Withheld: reading an answer needs draft access.
        </p>
      )}

      {note && <p className="mb-3 text-2xs leading-relaxed text-muted">{note}</p>}

      {state === "scored" && metrics.length > 0 ? (
        <div className="mb-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {metrics.map((metric) => (
            <div
              key={metric.key}
              className="rounded-card border border-line bg-surface px-3 py-2.5"
            >
              <div className="micro">{metric.label}</div>
              <div
                className="mt-[5px] text-kpi font-extrabold"
                style={{
                  color:
                    metric.tone === "bad"
                      ? "#B23438"
                      : metric.tone === "unmeasured"
                        ? "#A49DA8"
                        : "#030304",
                }}
              >
                {metric.display}
              </div>
              <div className="mt-0.5 text-2xs text-muted-soft">{metric.target}</div>
              <div className="mt-1 text-2xs leading-relaxed text-muted">
                {metric.detail || metric.explanation}
              </div>
            </div>
          ))}
        </div>
      ) : state === "pending" ? (
        <Spinner label="Scoring…" />
      ) : state === "failed" ? (
        <p className="mb-3 text-sm text-danger">
          Scoring failed: {live?.error ?? row.scoreError}
        </p>
      ) : (
        <p className="mb-3 text-sm text-muted">Not scored yet.</p>
      )}

      {row.contextLocators.length > 0 && (
        <>
          <div className="eyebrow mb-1 !text-muted">Passages in the prompt</div>
          <ol className="mb-3 space-y-0.5">
            {row.contextLocators.map((locator, n) => (
              <li key={`${locator}-${n}`} className="text-2xs text-muted">
                [{n + 1}] {locator}
              </li>
            ))}
          </ol>
        </>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button
          className="btn-primary !py-1.5"
          disabled={disabled}
          onClick={onScore}
          title={why}
        >
          {state === "pending"
            ? "Scoring…"
            : state
              ? "Re-evaluate"
              : "Evaluate"}
        </button>
        <span className="text-2xs text-muted-soft">
          {[
            row.model,
            `${(row.latencyMs / 1000).toFixed(1)}s`,
            `${row.chunksUsed} passages`,
            `${row.citationCount} cited`,
            row.judgeCalls ? `${row.judgeCalls} judge calls` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </div>
    </div>
  );
}

function RecentResponses() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [failingOnly, setFailingOnly] = useState(false);
  const [unscoredOnly, setUnscoredOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["responses", page, failingOnly, unscoredOnly],
    queryFn: () =>
      api.get<ResponseListResponse>("/admin/responses", {
        page,
        page_size: 15,
        range: "all",
        failing_only: failingOnly || undefined,
        state: unscoredOnly ? "unscored" : undefined,
      }),
    // No flash to a spinner when paging, which otherwise loses the reader's
    // place in a list they are working down.
    placeholderData: keepPreviousData,
  });

  const selected =
    data?.items.find((r) => r.messageId === selectedId) ?? data?.items[0] ?? null;

  // Only while something is actually in flight, and against the small endpoint
  // rather than re-fetching a page of fifteen answers every few seconds.
  const pendingId = selected?.scoreState === "pending" ? selected.messageId : null;
  const { data: live } = useQuery({
    queryKey: ["response-score", pendingId],
    queryFn: () =>
      api.get<ResponseScoreView>(`/admin/responses/${pendingId}/score`),
    enabled: !!pendingId,
    refetchInterval: (query) =>
      query.state.data?.state === "pending" ? 2500 : false,
  });

  useEffect(() => {
    if (live && live.state !== "pending") {
      void queryClient.invalidateQueries({ queryKey: ["responses"] });
    }
  }, [live?.state, live, queryClient]);

  const score = useMutation({
    mutationFn: (id: string) =>
      api.post<{ message: string }>(`/admin/responses/${id}/score`),
    onSuccess: (result) => {
      toast(result.message);
      void queryClient.invalidateQueries({ queryKey: ["responses"] });
    },
    onError: (caught) =>
      toast(
        caught instanceof Error ? caught.message : "Could not queue scoring",
        "danger",
      ),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  const from = data.total === 0 ? 0 : (page - 1) * data.pageSize + 1;
  const to = Math.min(page * data.pageSize, data.total);

  return (
    <>
      {data.judgeBlocked && <Banner tone="warn">{data.judgeBlocked}</Banner>}
      {data.judgeNotes.length > 0 && (
        <Banner tone="info">
          {data.judgeNotes.join(". ")}. Cited-context precision is still
          measured — it needs no model.
        </Banner>
      )}

      <Card
        title="Recent responses"
        meta="per-response metrics · evaluate on demand"
        padded={false}
        action={
          <span className="flex items-center gap-2">
            <button
              className="btn-tiny"
              aria-pressed={unscoredOnly}
              onClick={() => {
                setUnscoredOnly((on) => !on);
                setPage(1);
                setSelectedId(null);
              }}
            >
              {unscoredOnly ? "All" : "Not scored"}
            </button>
            <button
              className="btn-tiny"
              aria-pressed={failingOnly}
              onClick={() => {
                setFailingOnly((on) => !on);
                setPage(1);
                setSelectedId(null);
              }}
            >
              {failingOnly ? "All" : "Below floor"}
            </button>
            <button className="btn-tiny" onClick={() => void refetch()}>
              Refresh
            </button>
          </span>
        }
      >
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line-soft px-3 py-2 text-sm text-muted">
          <span>
            {from}–{to} of {formatNumber(data.total)} · {formatNumber(data.scoredTotal)}{" "}
            scored · {data.usedToday}/{data.dailyLimit} today
          </span>
          <span className="flex gap-2">
            <button
              className="btn-ghost !py-1"
              disabled={page <= 1}
              onClick={() => {
                setPage(page - 1);
                setSelectedId(null);
              }}
            >
              ‹ Prev
            </button>
            <button
              className="btn-ghost !py-1"
              disabled={page >= data.pageCount}
              onClick={() => {
                setPage(page + 1);
                setSelectedId(null);
              }}
            >
              Next ›
            </button>
          </span>
        </div>

        {data.items.length === 0 ? (
          <div className="px-4 py-6">
            <Empty title="Nothing to show" detail="No answers match this filter." />
          </div>
        ) : (
          /* An explicit max height on the list rather than a flex height
             chain: the admin pane is content-sized, so `h-full` here would
             resolve against the taller column and let the detail pane set the
             row height. */
          <div className="grid gap-4 p-3 lg:grid-cols-[minmax(0,340px)_1fr]">
            <ul className="max-h-[560px] overflow-y-auto overscroll-contain rounded-field border border-line">
              {data.items.map((row) => (
                <ResponseListRow
                  key={row.messageId}
                  row={row}
                  selected={selected?.messageId === row.messageId}
                  onSelect={() => setSelectedId(row.messageId)}
                />
              ))}
            </ul>
            {selected && (
              <ResponseDetail
                row={selected}
                live={live}
                scoring={score.isPending}
                blocked={data.judgeBlocked}
                atLimit={data.usedToday >= data.dailyLimit}
                onScore={() => score.mutate(selected.messageId)}
              />
            )}
          </div>
        )}
      </Card>
    </>
  );
}

const METRICS_EXPLAINER = `The first five cards come from the labelled suite — questions written in advance, with a known correct answer to check against. The sixth counts real answers that have been scored, which is a different denominator, so each caption names its own.

Context recall is measurable only on the suite. It asks what share of a correct answer the retrieved passages support, and a reader's question has no correct answer written down, so there is nothing to check against. It will always read "—" on a real response, and that is a property of the question rather than a gap to be filled.

Answers given before the retrieval record existed score on relevance and cited-context precision only. Faithfulness and context precision need the passages the model actually read, and for those answers they were never kept.`;

const SUITE_EXPLAINER = `Thirteen questions supplied by the client, run against the four documents in the compendium. Each carries a ground-truth answer, so context recall is measured rather than skipped, and ten name the document the answer must cite.

A run answers every question, then judges each answer: whether its claims are supported by the passages it was shown, whether it addressed the question, and whether retrieval put the right passages in front of it.`;

export function Evaluation() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["eval"],
    queryFn: () => api.get<EvalSummary>("/admin/eval"),
    // While a run is in flight, poll for it. The previous single four-second
    // timeout never saw a judged run, which takes minutes.
    refetchInterval: (query) => (query.state.data?.running ? 5000 : false),
  });

  // A Set, not one id: comparing two failing cases side by side is the reason
  // anybody opens this card.
  const [openCases, setOpenCases] = useState<Set<string>>(() => new Set());
  const [failuresOnly, setFailuresOnly] = useState(false);

  const run = useMutation({
    mutationFn: () => api.post<{ message: string }>("/admin/eval/run"),
    onSuccess: (result) => {
      toast(result.message);
      void queryClient.invalidateQueries({ queryKey: ["eval"] });
    },
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  return (
    <>
      <div className="mb-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {data.headline.map((card) => (
          <Metric
            key={card.key}
            label={card.label}
            value={card.value}
            trend={card.caption}
            tone={card.tone}
          />
        ))}
      </div>

      <Card
        className="mb-3"
        title="What these metrics mean"
        collapsible
        storageKey="eval-metrics-explainer"
        defaultOpen={false}
      >
        <p className="whitespace-pre-line text-sm leading-relaxed text-muted">
          {METRICS_EXPLAINER}
        </p>
      </Card>

      {data.running && (
        <Banner tone="info">
          A run is in progress. The figures above are from the last finished
          run, and will update when this one completes.
        </Banner>
      )}
      {data.promotionBlocked && !data.running && (
        <Banner tone="warn">
          One or more quality floors are not met, so this model would not be
          promoted. Failures are listed below for a human check.
        </Banner>
      )}

      <Card
        className="mb-3"
        title="Labelled eval-suite"
        meta={
          data.startedAt
            ? `Last run ${timeAgo(data.startedAt)} on ${data.model} · ${data.passCount} of ${data.total} passed (${data.passRate}%)`
            : "Never run"
        }
        info={{ title: "What is the labelled eval-suite?", text: SUITE_EXPLAINER }}
        collapsible
        storageKey="golden-set"
        defaultOpen
        padded={false}
        action={
          <span className="flex items-center gap-2">
            <button
              className="btn-tiny"
              onClick={() => setFailuresOnly((on) => !on)}
              aria-pressed={failuresOnly}
            >
              {failuresOnly ? "All questions" : "Failures only"}
            </button>
            <button
              className="btn-tiny"
              onClick={() =>
                setOpenCases((open) =>
                  open.size > 0
                    ? new Set()
                    : new Set(data.cases.map((c) => c.caseId)),
                )
              }
            >
              {openCases.size > 0 ? "Collapse all" : "Expand all"}
            </button>
            <button
              className="btn-primary !py-1.5"
              disabled={run.isPending || data.running}
              onClick={() => run.mutate()}
            >
              {run.isPending || data.running ? "Running…" : "▶ Run eval suite"}
            </button>
          </span>
        }
      >
        {data.runId === null ? (
          <div className="px-4 py-6">
            <Empty
              title="The suite has not been run yet"
              detail="The questions below are seeded and ready. Running them scores groundedness, citation accuracy, correct refusals and latency against the specification's floors."
            />
          </div>
        ) : (
          <>
            {data.cases.every((c) => c.passed === null) && (
              <div className="px-4 pt-3">
                <Banner tone="info">
                  The question set has changed since the last run — none of
                  these were scored by it. Run the suite to score the current
                  set.
                </Banner>
              </div>
            )}
            <ul>
              {(failuresOnly
                ? data.cases.filter((c) => c.passed === false)
                : data.cases
              ).map((entry) => (
                <CaseRow
                  key={entry.caseId}
                  entry={entry}
                  open={openCases.has(entry.caseId)}
                  onToggle={() =>
                    setOpenCases((open) => {
                      const next = new Set(open);
                      if (!next.delete(entry.caseId)) next.add(entry.caseId);
                      return next;
                    })
                  }
                />
              ))}
            </ul>
          </>
        )}
        <p className="border-t border-line-soft px-4 py-2.5 text-2xs leading-relaxed text-muted-soft">
          Each question is scored on its own. A metric shown as
          &ldquo;&mdash;&rdquo; was not measured — hover it for the reason —
          which is not the same as a score of zero. &ldquo;n/a&rdquo; on a check
          means it does not apply: a question that should be declined is never
          asked to cite, and a case with no expected document cannot miss one.
          A run against the mock provider exercises the harness but is not a
          measurement of answer quality.
        </p>
      </Card>

      <RecentResponses />
    </>
  );
}
